// src/components/Users/UserList.js
import React, { useEffect, useState, useContext } from 'react';
import { getUsuarios, deleteUsuario } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const UserList = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const data = await getUsuarios();
        if (data.success) {
          setUsuarios(data.data);
        } else {
          setError('Error al obtener usuarios');
        }
      } catch (err) {
        setError(err.error || 'Error al obtener usuarios');
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
  }, []);

  const handleEdit = (id) => {
    navigate(`/users/edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const data = await deleteUsuario(selectedId);
      if (data.success) {
        setUsuarios(usuarios.filter((usr) => usr.id !== selectedId));
      } else {
        setError('Error al eliminar usuario');
      }
    } catch (err) {
      setError(err.error || 'Error al eliminar usuario');
    } finally {
      setOpenDialog(false);
      setSelectedId(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };

  if (loading) return <CircularProgress />;

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={4} mb={2}>
        <Typography variant="h4">Usuarios</Typography>
        {user.permisos.crearUsuarios && (
          <Button variant="contained" color="primary" onClick={() => navigate('/users/new')}>
            Crear Usuario
          </Button>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Usuario</TableCell>
            <TableCell>Rol</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Último Acceso</TableCell>
            <TableCell>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usuarios.map((usuario) => (
            <TableRow key={usuario.id}>
              <TableCell>{usuario.id}</TableCell>
              <TableCell>{usuario.username}</TableCell>
              <TableCell>{usuario.rol}</TableCell>
              <TableCell>{usuario.estado}</TableCell>
              <TableCell>{usuario.ultimoAcceso ? new Date(usuario.ultimoAcceso).toLocaleString() : 'Nunca'}</TableCell>
              <TableCell>
                <Button variant="outlined" color="primary" onClick={() => handleEdit(usuario.id)}>
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleDeleteClick(usuario.id)}
                  style={{ marginLeft: '8px' }}
                >
                  Eliminar
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Diálogo de Confirmación para Eliminar */}
      <Dialog
        open={openDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserList;
